/**
 * Copyright 2022-2023, 2025 Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import http from 'http';
import https from 'https';
import url from 'url';
import decompressResponse from 'decompress-response';
import { REQUEST_TIMEOUT_MS } from '../enums';
import { NO_STATUS_CODE_IN_RESPONSE, REQUEST_ERROR, REQUEST_TIMEOUT, UNSUPPORTED_PROTOCOL } from 'error_message';
import { OptimizelyError } from '../../error/optimizly_error';
/**
 * Handles sending requests and receiving responses over HTTP via NodeJS http module
 */
export class NodeRequestHandler {
    constructor(opt = {}) {
        var _a;
        this.logger = opt.logger;
        this.timeout = (_a = opt.timeout) !== null && _a !== void 0 ? _a : REQUEST_TIMEOUT_MS;
    }
    /**
     * Builds an XMLHttpRequest
     * @param requestUrl Fully-qualified URL to which to send the request
     * @param headers List of headers to include in the request
     * @param method HTTP method to use
     * @param data? stringified version of data to POST, PUT, etc
     * @returns AbortableRequest contains both the response Promise and capability to abort()
     */
    makeRequest(requestUrl, headers, method, data) {
        const parsedUrl = url.parse(requestUrl);
        if (parsedUrl.protocol !== 'https:' && parsedUrl.protocol !== 'http:') {
            return {
                responsePromise: Promise.reject(new OptimizelyError(UNSUPPORTED_PROTOCOL, parsedUrl.protocol)),
                abort: () => { },
            };
        }
        const requestModule = parsedUrl.protocol === 'https:' ? https : http;
        const request = requestModule.request(Object.assign(Object.assign({}, this.getRequestOptionsFromUrl(parsedUrl)), { method, headers: Object.assign(Object.assign({}, headers), { 'accept-encoding': 'gzip,deflate', 'content-length': String((data === null || data === void 0 ? void 0 : data.length) || 0) }), timeout: this.timeout }));
        const abortableRequest = this.getAbortableRequestFromRequest(request);
        if (data) {
            request.write(data);
        }
        request.end();
        return abortableRequest;
    }
    /**
     * Parses a URL into its constituent parts
     * @param url URL object to parse
     * @private
     * @returns http.RequestOptions Standard request options dictionary compatible with both http and https
     */
    getRequestOptionsFromUrl(url) {
        return {
            hostname: url.hostname,
            path: url.path,
            port: url.port,
            protocol: url.protocol,
        };
    }
    /**
     * Parses headers from an http response
     * @param incomingMessage Incoming response message to parse
     * @private
     * @returns Headers Dictionary of headers without duplicates
     */
    createHeadersFromNodeIncomingMessage(incomingMessage) {
        const headers = {};
        Object.keys(incomingMessage.headers).forEach(headerName => {
            const headerValue = incomingMessage.headers[headerName];
            if (typeof headerValue === 'string') {
                headers[headerName] = headerValue;
            }
            else if (typeof headerValue === 'undefined') {
                // no value provided for this header
            }
            else {
                // array
                if (headerValue.length > 0) {
                    // We don't care about multiple values - just take the first one
                    headers[headerName] = headerValue[0];
                }
            }
        });
        return headers;
    }
    /**
     * Sends a built request handling response, errors, and events around the transmission
     * @param request Request to send
     * @private
     * @returns AbortableRequest with simplified response promise
     */
    getAbortableRequestFromRequest(request) {
        let aborted = false;
        const abort = () => {
            aborted = true;
            request.destroy();
        };
        const responsePromise = new Promise((resolve, reject) => {
            request.on('timeout', () => {
                aborted = true;
                request.destroy();
                reject(new OptimizelyError(REQUEST_TIMEOUT));
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            request.on('error', (err) => {
                if (err instanceof Error) {
                    reject(err);
                }
                else if (typeof err === 'string') {
                    reject(new Error(err));
                }
                else {
                    reject(new OptimizelyError(REQUEST_ERROR));
                }
            });
            request.once('response', (incomingMessage) => {
                if (aborted) {
                    return;
                }
                const response = decompressResponse(incomingMessage);
                response.setEncoding('utf8');
                let responseData = '';
                response.on('data', (chunk) => {
                    if (!aborted) {
                        responseData += chunk;
                    }
                });
                response.on('end', () => {
                    if (aborted) {
                        return;
                    }
                    if (!incomingMessage.statusCode) {
                        reject(new OptimizelyError(NO_STATUS_CODE_IN_RESPONSE));
                        return;
                    }
                    resolve({
                        statusCode: incomingMessage.statusCode,
                        body: responseData,
                        headers: this.createHeadersFromNodeIncomingMessage(incomingMessage),
                    });
                });
            });
        });
        return { abort, responsePromise };
    }
}
export const __platforms = ['node'];
//# sourceMappingURL=request_handler.node.js.map